import React, { useState } from "react";
//prettier-ignore
import {  MetricCard, AreaChart, Skeleton, MonthPickerWithPresets, Divider, DataTable } from "@components";
//prettier-ignore
import { Banknote, Package, Eye, RefreshCw, Users, Activity, Calendar, CircleSmall } from "lucide-react";
import { useFetch } from "@hooks/useFetch";
import { DashboardService } from "@services/DashboardService";
import { formatNumber } from "@utils/formatNumber";
import { useDistributorStore } from "@store/distributorStore";
import { usePartnerStore } from "@store/partnerStore";
import handHappyPay from "@assets/images/handHappypay.png";
import { DateRange } from "@models/DateRange";
import { DashboardMerchantTableColumns } from "./components/DashboardMerchantsTableColumns";

/*
 * Dashboard Component
 *--------------------
 * The dashboard component displays the performance metrics and analytics for Distributor
 * - Displaying the performance metrics for a selected time period (default current month)
 */

const dashboardService = new DashboardService();

export const DashboardPage: React.FC = () => {

  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    undefined
  );
  const [selectedPreset, setSelectedPreset] = useState<string>("");
  const [dateRange, setDateRange] = React.useState<DateRange>(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: firstDayOfMonth,
      to: today,
    };
  });
  const { distributorName } = useDistributorStore();
  const { currency } = usePartnerStore();

  const { data: dashboardData, isLoading: isDashboardLoading } = useFetch(
    (params: [string, string]) =>
      dashboardService.getDashboardDataByPeriod(params[0], params[1]),
    [
      dateRange.from.toLocaleDateString("en-CA"),
      dateRange.to.toLocaleDateString("en-CA"),
    ],
    [dateRange]
  );

   const { data: dashboardMerchantsData, isLoading: isDashboardMerchantsLoading } = useFetch(
    (params: [string, string]) =>
      dashboardService.getDashboardMerchants(params[0], params[1]),
    [
      dateRange.from.toLocaleDateString("en-CA"),
      dateRange.to.toLocaleDateString("en-CA"),
    ],
    [dateRange]
  ); 

  const formatDateFull = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Peformance helpers
  const impressions = Number(dashboardData?.exposureCount) || 0;
  const orders = Number(dashboardData?.orderGenerated) || 0;
  const revenueCost = Number(dashboardData?.revenueCost) || 0;

  const conversionRate = impressions ? (orders / impressions) * 100 : 0;

  // inclusive day count for the selected range
  const msPerDay = 1000 * 60 * 60 * 24;
  const dayCount = Math.max(
    1,
    Math.floor((dateRange.to.getTime() - dateRange.from.getTime()) / msPerDay) +
      1
  );
  const revenuePerDay = dayCount ? revenueCost / dayCount : 0;
  const ordersPerDay = dayCount ? orders / dayCount : 0;

  return (
    <div className="">


      {/* Header Section */}
      <div className="mb-4">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {distributorName}
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDateFull(dateRange.from.toISOString())} -{" "}
              {formatDateFull(dateRange.to.toISOString())}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <MonthPickerWithPresets
              selectedMonth={selectedMonth}
              selectedPreset={selectedPreset}
              onChange={(month: Date | undefined) => {
                setSelectedMonth(month);
                if (month) {
                  const start = new Date(
                    month.getFullYear(),
                    month.getMonth(),
                    1
                  );
                  const end = new Date(
                    month.getFullYear(),
                    month.getMonth() + 1,
                    0
                  );
                  setDateRange({ from: start, to: end });
                  setSelectedPreset("");
                }
              }}
              onPresetChange={(preset: string, range: DateRange) => {
                setSelectedPreset(preset);
                setDateRange(range);
                setSelectedMonth(undefined);
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Ads Widget + performance snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        {isDashboardLoading ? (
          <>
            <Skeleton className="h-[202px] w-full rounded-xl" />
            <Skeleton className="h-[202px] w-full rounded-xl" />
          </>
        ) : (
          <>
            <div className="relative overflow-hidden bg-[#0b0a12] rounded-xl p-8 shadow-sm border flex-1">
              {/* Soft purple glow background */}
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#15112b]" />
                <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-[#15112b]" />
              </div>

              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-100 mb-2">
                      Ads Revenue
                    </h2>
                  </div>

                  {/* Icon chip with purple accent */}
                  <div className="p-4 rounded-xl bg-white/10">
                    <Banknote className="w-8 h-8 text-white" />
                  </div>
                </div>

                <div className="flex items-baseline gap-4">
                  <span className="text-5xl font-semibold text-white">
                    {formatNumber(Number(dashboardData?.revenueCost) || 0)}
                  </span>
                  <span className="text-2xl text-gray-400 font-medium">
                    {currency}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-6 bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <div className="w-2/3">
                {/* Title and description*/}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                      Performance Snapshot
                    </h2>
                    <p className="description-sm">Daily performance metrics</p>
                  </div>
                </div>

                {/* Row stats */}
                <div className="flex items-center justify-between py-4 px-6 bg-gray-50 rounded-xl flex-2">
                  {/* Conversion */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white">
                      <RefreshCw className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-1">
                        Conversion
                      </div>
                      <div className="text-xl font-semibold text-gray-900">
                        {conversionRate.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-px h-12 bg-gray-200"></div>

                  {/* Revenue per Day */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white">
                      <Calendar className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-1">
                        Revenue / Day
                      </div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatNumber(revenuePerDay)}{" "}
                        <span className="text-sm font-normal text-gray-600">
                          {currency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Snapshot Widget */}
              <div className="flex flex-col justify-end gap-1 w-1/3 bg-[#0b0a12] rounded-xl shadow-sm border">
                <div className="mb-1 mx-4">
                  <div className="text-base font-medium text-white mb-1">
                    Orders / Day
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-medium text-white mb-4 ml-4">
                    {formatNumber(ordersPerDay)}
                  </div>
                  <img
                    src={handHappyPay}
                    alt="Hand happy pay"
                    className="w-auto h-28 object-cover"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {isDashboardLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm border"
            >
              <Skeleton className="h-5 w-28 mb-3" />
              <Skeleton className="h-8 w-40" />
            </div>
          ))
        ) : (
          <>
            <MetricCard
              title="Revenue Driven"
              value={Number(dashboardData?.revenueDriven) || 0}
              icon={Users}
              isMonetary={true}
              toolTipText="The total revenue generated by Ads Service"
            />
            <MetricCard
              title="Orders generated"
              value={Number(dashboardData?.orderGenerated) || 0}
              icon={Package}
            />
            <MetricCard
              title="Average order value"
              value={
                Number(
                  dashboardData?.revenueDriven / dashboardData?.orderGenerated
                ) || 0
              }
              isMonetary={true}
              icon={Activity}
            />
            <MetricCard
              title="Marketplace impressions"
              value={Number(dashboardData?.exposureCount) || 0}
              icon={Eye}
            />
          </>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Revenue trend
              </h3>
              <p className="text-sm text-gray-600">Accumulated</p>
            </div>
          </div>
          <div className="">
            {isDashboardLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <Skeleton className="w-full h-[357px]" />
              </div>
            ) : (
              <AreaChart
                data={dashboardData?.revenueTrend ?? []}
                lineColor="var(--chart-1)"
                showCurrency={true}
              />
            )}
          </div>
        </div>

        {/* Impressions Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Marketplace impressions trend
              </h3>
              <p className="text-sm text-gray-600">
                Product views on the marketplace
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
              <CircleSmall className="w-4 h-4 animate-pulse text-green-600" />
              <span className="text-sm font-medium text-green-600">Active</span>
            </div>
          </div>
          <div className="">
            {isDashboardLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <Skeleton className="w-full h-[260px]" />
              </div>
            ) : (
              <AreaChart
                data={dashboardData?.impressionTrend ?? []}
                lineColor="var(--chart-2)"
              />
            )}
          </div>
        </div>
      </div>

      {/* Performance by merchant */}
      <Divider margin={16} />
      <h1 className="text-3xl font-bold text-gray-900 mb-2 mt-8"> By Merchant</h1> 
      
       <div>
        <DataTable
          columns={DashboardMerchantTableColumns()}
          data={dashboardMerchantsData ?? []}
          isLoading={isDashboardMerchantsLoading}
          height="500px"
        />
      </div> 

    </div>
  );
};
